import { Business as Building2, Delete as DeleteIcon, Edit as EditIcon, Place as MapPin, People as Users } from '@mui/icons-material';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { Link, createFileRoute, useNavigate } from '@tanstack/react-router';
import { Suspense, useState } from 'react';
import { useTranslation } from 'react-i18next';

import AuthGuard from '@/components/auth-guard';
import { EventImplicationsSection } from '@/components/event-implications/event-implications-section';
import { DeleteConfirmationModal } from '@/components/mui-delete-confirmation-modal';
import { Page } from '@/components/page';
import { useEventAccessLayer } from '@/features/events';
import { useToast } from '@/hooks/use-toast';
import { useNovelRepository } from '@/hooks/useNovelRepository';
import { EntitySummary, SectionDefinition } from '@/lib/types';

export const Route = createFileRoute('/events/view/$id')({
  component: EventViewPage,
});

function EventViewContent() {
  const { id: eventId } = Route.useParams();
  const { novel, getItemRef, getSummaries } = useNovelRepository();
  const { t } = useTranslation();

  const { toast } = useToast();
  const navigate = useNavigate();
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const {
    event,
    sections,
    isLoading,
    deleteEvent,
  } = useEventAccessLayer(eventId);

  const eventLoading = isLoading;
  const loadingSettings = isLoading;

  const handleDelete = async () => {
    if (!eventId) return;
    setIsDeleting(true);
    try {
      await deleteEvent(eventId);
      toast({
        title: t('entity_form.events.delete_success_title'),
        description: t('entity_form.events.delete_success_desc'),
      });
      navigate({ to: '/events' });
    } catch (error) {
      console.error('Delete error:', error);
      toast({
        title: t('entity_form.common.delete_failed_title'),
        description: error instanceof Error ? error.message : t('entity_form.common.unexpected_error'),
        variant: 'destructive',
      });
      setIsDeleting(false);
    }
  };

  // Use pre-sorted summaries from repository
  const locations = getSummaries<EntitySummary>('locations');
  const characters = getSummaries<EntitySummary>('characters');
  const organizations = getSummaries<EntitySummary>('organizations');

  if (eventLoading || loadingSettings || (!event && !novel.id)) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress size={32} />
      </Box>
    );
  }

  if (!event) {
    return (
      <Box sx={{ p: 4, textAlign: 'center' }}>
        <Typography variant="h3" color="text.secondary">{t('entity_view.events.not_found_title')}</Typography>
        <Typography variant="body1">{t('entity_view.events.not_found_desc')}</Typography>
        <Button component={Link} to="/events" sx={{ mt: 2 }}>{t('entity_view.events.back')}</Button>
      </Box>
    );
  }

  return (
    <Page
      title={event.name}
      description={event.date || t('entity_view.events.view_fallback')}
      breadcrumbs={[
        { href: '/events', label: t('entity_list.events.title') },
        { label: event.name || t('entity_view.events.view_fallback') }
      ]}
      layoutMode="scrolling"
      actions={
        <Stack direction="row" spacing={2}>
          <Button
            variant="outlined"
            color="error"
            onClick={() => setShowDeleteConfirm(true)}
            startIcon={<DeleteIcon sx={{ fontSize: 18 }} />}
            sx={{ borderRadius: 2 }}
          >
            {t('entity_form.common.delete')}
          </Button>
          <Button
            variant="contained"
            component={Link}
            to={`/events/edit/${event.id}`}
            startIcon={<EditIcon sx={{ fontSize: 18 }} />}
            sx={{ borderRadius: 2 }}
          >
            Edit Event
          </Button>
        </Stack>
      }
    >

      <Card sx={{ borderRadius: 3, border: '1px solid', borderColor: 'divider', overflow: 'hidden' }}>
        <CardContent sx={{ p: 4 }}>
          <Stack spacing={4}>
            {event.description && (
              <Box>
                <Typography variant="overline" color="text.secondary" fontWeight={700}>{t('entity_view.events.labels.description')}</Typography>
                <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap', mt: 1 }}>{event.description}</Typography>
              </Box>
            )}

            {event.shortDescription && (
              <Box>
                <Typography variant="overline" color="text.secondary" fontWeight={700}>{t('entity_view.events.labels.summary')}</Typography>
                <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap', mt: 1 }}>{event.shortDescription}</Typography>
              </Box>
            )}

            {event.whyItMatters && (
              <Box sx={{ p: 3, bgcolor: 'primary.main', color: 'primary.contrastText', borderRadius: 2 }}>
                <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 700 }}>{t('entity_view.events.labels.significance')}</Typography>
                <Typography variant="body1" sx={{ fontStyle: 'italic' }}>{event.whyItMatters}</Typography>
              </Box>
            )}

            {/* Custom Fields */}
            {sections.map((section: SectionDefinition) => {
              const sectionFieldsWithValues = section.fields.filter((f) => event.details?.[f.id]);
              if (sectionFieldsWithValues.length === 0) return null;

              return (
                <Box key={section.id}>
                  <Typography variant="overline" color="text.secondary" fontWeight={700} sx={{ display: 'block', borderBottom: '1px solid', borderColor: 'divider', mb: 2 }}>
                    {section.title}
                  </Typography>
                  <Stack spacing={3}>
                    {sectionFieldsWithValues.map((field) => (
                      <Box key={field.id}>
                        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700, textTransform: 'uppercase' }}>{field.label}</Typography>
                        <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap', mt: 0.5 }}>{event.details?.[field.id] as string}</Typography>
                      </Box>
                    ))}
                  </Stack>
                </Box>
              );
            })}

            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr 1fr' }, gap: 4, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
              {[
                { ids: event.locationIds, items: locations, label: t('entity_view.events.labels.locations'), icon: <MapPin sx={{ fontSize: 18, color: 'primary.main' }} />, path: '/locations/view' },
                { ids: event.characterIds, items: characters, label: t('entity_view.events.labels.characters'), icon: <Users sx={{ fontSize: 18, color: 'primary.main' }} />, path: '/characters/view' },
                { ids: event.organizationIds, items: organizations, label: t('entity_view.events.labels.organizations'), icon: <Building2 sx={{ fontSize: 18, color: 'primary.main' }} />, path: '/organizations/view' },
              ].map(({ ids, items, label, icon, path }, index) => ids && ids.length > 0 && (
                <Box key={index}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                    {icon}
                    <Typography variant="subtitle2" fontWeight={700}>{label}</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {ids.map((id) => {
                      const item = items?.find((x) => x.id === id);
                      return item ? <Chip key={id} label={item.name} size="small" variant="outlined" component={Link} to={`${path}/${id}`} sx={{ cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' } }} /> : null;
                    })}
                  </Box>
                </Box>
              ))}
            </Box>

            {novel?.id && event && (
              <Box sx={{ pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
                <EventImplicationsSection
                  eventId={eventId}
                  eventData={event}
                  readOnly
                />
              </Box>
            )}
          </Stack>
        </CardContent>
      </Card>
      <DeleteConfirmationModal
        isOpen={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        onConfirm={handleDelete}
        isDeleting={isDeleting}
        itemName={event?.name}
        description={t('entity_form.events.delete_desc')}
      />
    </Page>
  );
}

function EventViewPage() {
  return (
    <AuthGuard>
      <Suspense fallback={
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress size={32} />
        </Box>
      }>
        <EventViewContent />
      </Suspense>
    </AuthGuard>
  );
}
